import React, { useEffect, useState, memo, Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageHeader from '../components/layout/PageHeader';
import Button from '../components/ui/Button';
import { CalendarIcon, ClockIcon, MapPinIcon, UsersIcon, TagIcon, ShareIcon, ArrowLeftIcon, FacebookIcon, TwitterIcon, LinkedinIcon } from 'lucide-react';
// Sample events data
const eventsData = [{
  id: 1,
  slug: 'sports-competition',
  title: 'Annual Inter-House Sports Competition',
  date: '2023-11-10',
  time: '9:00 AM - 4:00 PM',
  location: 'School Sports Ground',
  category: 'sports',
  tags: ['Sports', 'Competition', 'Inter-House'],
  image: 'https://placehold.co/1200x800/navy/white?text=Sports+Competition',
  description: 'Join us for our annual Inter-House Sports Competition featuring athletics, football, volleyball, and more exciting sporting activities...',
  content: `<p>We are excited to announce that the Annual Inter-House Sports Competition for the 2023/2024 academic session will be held on November 10, 2023, at the school sports ground.</p>
    <p>This highly anticipated event brings together students, staff, parents, and alumni for a day of friendly competition, sportsmanship, and school spirit. The four houses - Ransome-Kuti (Red), Awolowo (Blue), Azikiwe (Green), and Bello (Yellow) - will compete for the coveted Champions Trophy.</p>
    <h3>Event Details:</h3>
    <ul>
      <li><strong>Date:</strong> November 10, 2023</li>
      <li><strong>Time:</strong> 9:00 AM - 4:00 PM</li>
      <li><strong>Venue:</strong> School Sports Ground</li>
      <li><strong>Chief Host:</strong> Mrs. Adebisi Ogunlana, Principal</li>
      <li><strong>Special Guest of Honor:</strong> Dr. Oluwasegun Adeyemi, Chairman, School Board of Governors</li>
    </ul>
    <h3>Competition Categories:</h3>
    <ul>
      <li>Track Events (100m, 200m, 400m, 800m, relays)</li>
      <li>Field Events (long jump, high jump, shot put, javelin)</li>
      <li>Team Sports (football, volleyball, basketball)</li>
      <li>Special Events (tug-of-war, sack race, egg-and-spoon race)</li>
      <li>Staff and Parents' Race</li>
    </ul>
    <p>Last year, Awolowo House emerged as the overall winner with Ransome-Kuti House as the first runner-up. Which house will claim the trophy this year?</p>
    <p>Parents, guardians, alumni, and friends of the school are cordially invited to attend and support the students. Refreshments will be available.</p>
    <p>For more information, please contact the Sports Department or the House Masters.</p>
    <p>Come in your house colors and let's make this a memorable day of fun, fitness, and friendly competition!</p>`
}, {
  id: 2,
  slug: 'cultural-day',
  title: 'Cultural Day Celebration',
  date: '2023-12-05',
  time: '10:00 AM',
  location: 'School Hall',
  category: 'cultural',
  tags: ['Culture', 'Celebration', 'Tradition'],
  image: 'https://placehold.co/1200x800/darkblue/white?text=Cultural+Day',
  description: "A colorful celebration of Nigeria's diverse cultures featuring traditional dances, attire, and cuisine.",
  content: `<p>Isonyin Grammar School is pleased to announce our annual Cultural Day Celebration, scheduled for December 5, 2023, at the School Hall.</p>
    <p>Cultural Day is one of the most colorful and vibrant events in our school calendar. It's a day when we celebrate Nigeria's rich cultural diversity and heritage through various performances, exhibitions, and activities.</p>
    <h3>Event Details:</h3>
    <ul>
      <li><strong>Date:</strong> December 5, 2023</li>
      <li><strong>Time:</strong> 10:00 AM - 3:00 PM</li>
      <li><strong>Venue:</strong> School Hall</li>
      <li><strong>Theme:</strong> "Celebrating Our Diversity, Strengthening Our Unity"</li>
    </ul>
    <h3>Program Highlights:</h3>
    <ul>
      <li>Traditional dance performances representing various Nigerian ethnic groups</li>
      <li>Cultural fashion parade showcasing traditional attires</li>
      <li>Exhibition of cultural artifacts and crafts</li>
      <li>Food fair featuring diverse Nigerian cuisines</li>
      <li>Cultural quiz competition</li>
      <li>Drama presentations depicting cultural values and traditions</li>
    </ul>
    <p>Students will be grouped according to different Nigerian cultures, not necessarily their own, to promote cross-cultural understanding and appreciation. Each group will showcase the dress, food, dance, music, and traditions of their assigned culture.</p>
    <p>Parents, guardians, alumni, and friends of the school are cordially invited to attend this colorful event. We encourage everyone to come dressed in traditional attire representing any Nigerian culture.</p>
    <p>For further inquiries, please contact the Cultural Day Planning Committee through the school office.</p>
    <p>Let's celebrate our rich cultural heritage together!</p>`
}, {
  id: 3,
  slug: 'carol-service',
  title: 'Christmas Carol Service',
  date: '2023-12-12',
  time: '4:00 PM',
  location: 'School Chapel',
  category: 'religious',
  tags: ['Christmas', 'Carol', 'Celebration'],
  image: 'https://placehold.co/1200x800/blue/white?text=Christmas+Carol',
  description: 'Annual carol service featuring performances by the school choir and drama group.',
  content: `<p>Isonyin Grammar School invites you to our Annual Christmas Carol Service, a special evening of music, reflection, and celebration of the Christmas season.</p>
    <p>Join us as our school choir, orchestra, and drama group present beautiful renditions of traditional and contemporary Christmas carols, along with dramatic presentations of the Christmas story.</p>
    <h3>Event Details:</h3>
    <ul>
      <li><strong>Date:</strong> December 12, 2023</li>
      <li><strong>Time:</strong> 4:00 PM - 6:30 PM</li>
      <li><strong>Venue:</strong> School Chapel</li>
      <li><strong>Theme:</strong> "The Light of Christmas"</li>
    </ul>
    <h3>Program Highlights:</h3>
    <ul>
      <li>Processional hymn and lighting of the Advent candles</li>
      <li>Scripture readings recounting the Christmas story</li>
      <li>Choral performances by the Junior and Senior Choirs</li>
      <li>Instrumental performances by the School Orchestra</li>
      <li>Drama presentation: "The First Christmas"</li>
      <li>Community carol singing</li>
      <li>Special address by the Principal</li>
      <li>Benediction and recessional</li>
    </ul>
    <p>This event is open to all students, staff, parents, alumni, and friends of the school. Light refreshments will be served after the service.</p>
    <p>As part of our commitment to giving back to the community, we will be collecting non-perishable food items and toys to donate to local orphanages. We encourage attendees to bring along their donations.</p>
    <p>For more information, please contact the Music Department or the School Chaplain.</p>
    <p>Come and experience the joy and wonder of Christmas with us!</p>`
}];
const EventDetail = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const [event, setEvent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([]);
  useEffect(() => {
    // Simulate API call to fetch event
    setLoading(true);
    // Find the event with the matching slug
    const foundEvent = eventsData.find(event => event.slug === slug);
    if (foundEvent) {
      setEvent(foundEvent);
      // Find upcoming events (excluding current event)
      const upcoming = eventsData.filter(e => e.id !== foundEvent.id).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).slice(0, 3);
      setUpcomingEvents(upcoming);
    }
    setLoading(false);
  }, [slug]);
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = event?.title || 'Event at Isonyin Grammar School';
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${url}&text=${title}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`, '_blank');
        break;
      default:
        // Copy link to clipboard
        navigator.clipboard.writeText(url).then(() => alert('Link copied to clipboard!')).catch(err => console.error('Failed to copy: ', err));
    }
  };
  const handleAddToCalendar = () => {
    if (!event) return;
    alert('Calendar feature would open Google Calendar with this event');
  };
  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center" data-id="element-3408">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-900" data-id="element-3409"></div>
      </div>;
  }
  if (!event) {
    return <div className="min-h-screen bg-white" data-id="element-3410">
        <PageHeader title="Event Not Found" description="The event you are looking for does not exist or has been moved." backgroundImage="https://placehold.co/1200x800/gray/white?text=Not+Found" breadcrumbs={[{
        label: 'Home',
        path: '/'
      }, {
        label: 'Events',
        path: '/events'
      }, {
        label: 'Not Found',
        path: `/events/${slug}`
      }]} data-id="element-3411" />
        <div className="container mx-auto px-4 py-12 text-center" data-id="element-3412">
          <h2 className="text-3xl font-bold text-blue-900 mb-6" data-id="element-3413">
            Event Not Found
          </h2>
          <p className="text-gray-700 mb-8" data-id="element-3414">
            The event you are looking for does not exist or has been moved.
          </p>
          <Button href="/events" variant="primary" icon={<ArrowLeftIcon data-id="element-3416" />} iconPosition="left" data-id="element-3415">
            Back to Events
          </Button>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-white" data-id="element-3417">
      <PageHeader title={event.title} description={event.description} backgroundImage={event.image} breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Events',
      path: '/events'
    }, {
      label: event.title,
      path: `/events/${event.slug}`
    }]} data-id="element-3418" />
      <div className="container mx-auto px-4 py-12" data-id="element-3419">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-3420">
          <div className="lg:col-span-2" data-id="element-3421">
            <article className="bg-white rounded-lg shadow-md p-6 lg:p-8" data-id="element-3422">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6" data-id="element-3423">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3424">
                  <div className="flex items-center" data-id="element-3425">
                    <CalendarIcon className="h-5 w-5 text-blue-700 mr-3 flex-shrink-0" data-id="element-3426" />
                    <div data-id="element-3427">
                      <p className="text-sm text-gray-500" data-id="element-3428">Date</p>
                      <p className="font-bold text-gray-900" data-id="element-3429">
                        {formatDate(event.date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-3430">
                    <ClockIcon className="h-5 w-5 text-blue-700 mr-3 flex-shrink-0" data-id="element-3431" />
                    <div data-id="element-3432">
                      <p className="text-sm text-gray-500" data-id="element-3433">Time</p>
                      <p className="font-bold text-gray-900" data-id="element-3434">{event.time}</p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-3435">
                    <MapPinIcon className="h-5 w-5 text-blue-700 mr-3 flex-shrink-0" data-id="element-3436" />
                    <div data-id="element-3437">
                      <p className="text-sm text-gray-500" data-id="element-3438">Location</p>
                      <p className="font-bold text-gray-900" data-id="element-3439">
                        {event.location}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-blue-100" data-id="element-3440">
                  <Button onClick={handleAddToCalendar} variant="secondary" size="sm" data-id="element-3441">
                    Add to Calendar
                  </Button>
                  <Button href={`mailto:?subject=${encodeURIComponent(`Join me at ${event.title}`)}&body=${encodeURIComponent(`I'd like to invite you to ${event.title} at Isonyin Grammar School on ${formatDate(event.date)} at ${event.time}. More details: ${window.location.href}`)}`} variant="outline" size="sm" data-id="element-3442">
                    Invite Friends
                  </Button>
                </div>
              </div>
              <div className="prose prose-blue max-w-none mb-8" dangerouslySetInnerHTML={{
              __html: event.content
            }} data-id="element-3443" />
              <div className="border-t border-gray-200 pt-6 mt-8" data-id="element-3444">
                <div className="flex flex-wrap justify-between items-center" data-id="element-3445">
                  <div className="mb-4 md:mb-0" data-id="element-3446">
                    <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-3447">
                      Tags:
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-3448">
                      {event.tags.map((tag: string, index: number) => <Link key={index} to={`/events?tag=${tag}`} className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm flex items-center transition-colors" data-id="element-3449">
                          <TagIcon className="h-3 w-3 mr-1" data-id="element-3450" />
                          {tag}
                        </Link>)}
                    </div>
                  </div>
                  <div data-id="element-3451">
                    <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-3452">
                      Share:
                    </p>
                    <div className="flex space-x-2" data-id="element-3453">
                      <button onClick={() => handleShare('facebook')} className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" aria-label="Share on Facebook" data-id="element-3454">
                        <FacebookIcon className="h-4 w-4" data-id="element-3455" />
                      </button>
                      <button onClick={() => handleShare('twitter')} className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" aria-label="Share on Twitter" data-id="element-3456">
                        <TwitterIcon className="h-4 w-4" data-id="element-3457" />
                      </button>
                      <button onClick={() => handleShare('linkedin')} className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" aria-label="Share on LinkedIn" data-id="element-3458">
                        <LinkedinIcon className="h-4 w-4" data-id="element-3459" />
                      </button>
                      <button onClick={() => handleShare('copy')} className="p-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-colors" aria-label="Copy link" data-id="element-3460">
                        <ShareIcon className="h-4 w-4" data-id="element-3461" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </article>
            <div className="mt-8" data-id="element-3462">
              <Button href="/events" variant="outline" icon={<ArrowLeftIcon data-id="element-3464" />} iconPosition="left" data-id="element-3463">
                Back to Events
              </Button>
            </div>
          </div>
          <div data-id="element-3465">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-id="element-3466">
              <h3 className="text-xl font-bold text-blue-900 mb-4 border-b border-gray-200 pb-2" data-id="element-3467">
                Upcoming Events
              </h3>
              {upcomingEvents.length > 0 ? <div className="space-y-4" data-id="element-3468">
                  {upcomingEvents.map(upcomingEvent => <div key={upcomingEvent.id} className="flex items-start" data-id="element-3469">
                      <div className="bg-blue-100 text-blue-900 font-bold rounded p-2 text-center min-w-[50px] mr-3 flex-shrink-0" data-id="element-3470">
                        <div className="text-lg" data-id="element-3471">
                          {new Date(upcomingEvent.date).getDate()}
                        </div>
                        <div className="text-xs" data-id="element-3472">
                          {new Date(upcomingEvent.date).toLocaleString('default', {
                      month: 'short'
                    }).toUpperCase()}
                        </div>
                      </div>
                      <div data-id="element-3473">
                        <h4 className="font-bold text-blue-900 hover:text-blue-700 transition-colors line-clamp-2" data-id="element-3474">
                          <Link to={`/events/${upcomingEvent.slug}`} data-id="element-3475">
                            {upcomingEvent.title}
                          </Link>
                        </h4>
                        <div className="flex items-center text-sm text-gray-500 mt-1" data-id="element-3476">
                          <ClockIcon className="h-3 w-3 mr-1" data-id="element-3477" />
                          <span data-id="element-3478">{upcomingEvent.time}</span>
                        </div>
                      </div>
                    </div>)}
                </div> : <p className="text-gray-700" data-id="element-3479">No upcoming events.</p>}
              <div className="mt-6" data-id="element-3480">
                <Button href="/events" variant="outline" size="sm" className="w-full" data-id="element-3481">
                  View All Events
                </Button>
              </div>
            </div>
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-lg p-6" data-id="element-3482">
              <div className="flex items-center mb-4" data-id="element-3483">
                <UsersIcon className="h-6 w-6 text-yellow-400 mr-3" data-id="element-3484" />
                <h3 className="text-xl font-bold" data-id="element-3485">Interested in Attending?</h3>
              </div>
              <p className="mb-6" data-id="element-3486">
                Let us know if you're planning to attend this event. This helps
                us with planning and preparations.
              </p>
              <form className="space-y-3" onSubmit={e => {
              e.preventDefault();
              alert('Thank you for your interest! We look forward to seeing you at the event.');
            }} data-id="element-3487">
                <div data-id="element-3488">
                  <input type="text" placeholder="Your Name" className="w-full px-4 py-2 rounded-md border border-blue-700 bg-blue-800 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent" required data-id="element-3489" />
                </div>
                <div data-id="element-3490">
                  <input type="email" placeholder="Your Email" className="w-full px-4 py-2 rounded-md border border-blue-700 bg-blue-800 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent" required data-id="element-3491" />
                </div>
                <div data-id="element-3492">
                  <select className="w-full px-4 py-2 rounded-md border border-blue-700 bg-blue-800 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent" required data-id="element-3493">
                    <option value="" data-id="element-3494">Number of Attendees</option>
                    <option value="1" data-id="element-3495">1</option>
                    <option value="2" data-id="element-3496">2</option>
                    <option value="3" data-id="element-3497">3</option>
                    <option value="4" data-id="element-3498">4</option>
                    <option value="5+" data-id="element-3499">5+</option>
                  </select>
                </div>
                <Button type="submit" variant="primary" className="w-full" data-id="element-3500">
                  I'll Be There
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default EventDetail;